import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy } from '@angular/core';

import {
  CAROUSEL_AUTOPLAY_INTERACTION_PAUSE_MS,
  CAROUSEL_AUTOPLAY_INTERVAL_MS,
  CAROUSEL_AUTOPLAY_START_DELAY_MS,
  onReducedMotionChange,
  prefersReducedMotion,
} from '../../core/utils/carousel-autoplay.util';
import {
  getScrollStepFromCard,
  getHorizontalScrollPosition,
  scrollElementHorizontalCircular,
} from '../../core/utils/carousel-scroll.util';

/**
 * Advances a horizontal scroll track one item at a time, wrapping at the end.
 *
 * This is the single auto-scroll mechanism for every rail on the storefront.
 * Apply it to the scrolling element itself (the one with `overflow-x: auto`).
 *
 * Autoplay yields to the visitor: it pauses on hover, keyboard focus, wheel or
 * touch interaction, while the tab is hidden, while the rail is off-screen, and
 * whenever the OS asks for reduced motion.
 */
@Directive({
  selector: '[appAutoScroll]',
  standalone: true,
})
export class AutoScrollDirective implements AfterViewInit, OnDestroy {
  /** Selector of a single item, used to advance by exactly one card + gap. */
  @Input() appAutoScrollItem = '';
  /** Fraction of the track width to advance when no item matches the selector. */
  @Input() appAutoScrollStepRatio = 0.25;
  @Input() appAutoScrollIntervalMs = CAROUSEL_AUTOPLAY_INTERVAL_MS;
  @Input() appAutoScrollDisabled = false;

  private tickId: ReturnType<typeof setInterval> | null = null;
  private startId: ReturnType<typeof setTimeout> | null = null;
  private interactionResumeId: ReturnType<typeof setTimeout> | null = null;
  private observer: IntersectionObserver | null = null;
  private teardownReducedMotion: (() => void) | null = null;
  private readonly teardownListeners: Array<() => void> = [];

  private pointerInside = false;
  private keyboardFocused = false;
  private interacting = false;
  private offScreen = true;
  private reducedMotion = false;

  constructor(private readonly host: ElementRef<HTMLElement>, private readonly zone: NgZone) {}

  ngAfterViewInit(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.reducedMotion = prefersReducedMotion();
    this.teardownReducedMotion = onReducedMotionChange((reduced) => {
      this.reducedMotion = reduced;
    });

    // Autoplay only mutates scrollLeft, so it never needs a change-detection pass.
    this.zone.runOutsideAngular(() => {
      this.bindInteractionListeners();
      this.observeVisibility();
      this.startId = setTimeout(() => this.start(), CAROUSEL_AUTOPLAY_START_DELAY_MS);
    });
  }

  ngOnDestroy(): void {
    this.stop();
    this.clearTimeout(this.startId);
    this.startId = null;
    this.clearTimeout(this.interactionResumeId);
    this.interactionResumeId = null;
    this.observer?.disconnect();
    this.observer = null;
    this.teardownReducedMotion?.();
    this.teardownReducedMotion = null;
    this.teardownListeners.forEach((teardown) => teardown());
    this.teardownListeners.length = 0;
  }

  private start(): void {
    this.stop();
    this.tickId = setInterval(
      () => this.tick(),
      Math.max(1500, this.appAutoScrollIntervalMs)
    );
  }

  private stop(): void {
    if (this.tickId != null) {
      clearInterval(this.tickId);
      this.tickId = null;
    }
  }

  private tick(): void {
    if (!this.canAdvance()) {
      return;
    }

    const track = this.host.nativeElement;
    const step = getScrollStepFromCard(
      track,
      this.appAutoScrollItem,
      this.appAutoScrollStepRatio
    );
    scrollElementHorizontalCircular(track, 'next', step);
  }

  private canAdvance(): boolean {
    if (
      this.appAutoScrollDisabled ||
      this.reducedMotion ||
      this.pointerInside ||
      this.keyboardFocused ||
      this.interacting ||
      this.offScreen
    ) {
      return false;
    }

    if (typeof document !== 'undefined' && document.hidden) {
      return false;
    }

    // Nothing to do when the items already fit without scrolling.
    return getHorizontalScrollPosition(this.host.nativeElement).max > 4;
  }

  private bindInteractionListeners(): void {
    const track = this.host.nativeElement;

    this.listen(track, 'pointerenter', () => {
      this.pointerInside = true;
    });
    this.listen(track, 'pointerleave', () => {
      this.pointerInside = false;
    });
    this.listen(track, 'focusin', () => {
      this.keyboardFocused = true;
    });
    this.listen(track, 'focusout', () => {
      this.keyboardFocused = false;
    });

    // Wheel and touch mean the visitor is driving; back off for a while.
    const holdOff = (): void => this.holdOffAfterInteraction();
    this.listen(track, 'wheel', holdOff, { passive: true });
    this.listen(track, 'touchstart', holdOff, { passive: true });
    this.listen(track, 'touchmove', holdOff, { passive: true });
  }

  private holdOffAfterInteraction(): void {
    this.interacting = true;
    this.clearTimeout(this.interactionResumeId);
    this.interactionResumeId = setTimeout(() => {
      this.interacting = false;
      this.interactionResumeId = null;
    }, CAROUSEL_AUTOPLAY_INTERACTION_PAUSE_MS);
  }

  private observeVisibility(): void {
    if (typeof IntersectionObserver === 'undefined') {
      this.offScreen = false;
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === this.host.nativeElement) {
            this.offScreen = !entry.isIntersecting;
          }
        }
      },
      { root: null, rootMargin: '0px', threshold: 0.2 }
    );
    this.observer.observe(this.host.nativeElement);
  }

  private listen(
    target: HTMLElement,
    type: string,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): void {
    target.addEventListener(type, handler, options);
    this.teardownListeners.push(() => target.removeEventListener(type, handler, options));
  }

  private clearTimeout(id: ReturnType<typeof setTimeout> | null): void {
    if (id != null) {
      clearTimeout(id);
    }
  }
}
